import { createId } from '../lib/ids';
import { defaultLayer } from '../visualizer/layers';
import { presetById } from '../visualizer/presets';
import { createSession, putSession, sessionOf } from './avOps';
import { sceneEditorDefaults } from './factories';
import { OpError } from './projectOps';
import { addScene } from './sceneOps';
import {
  MAX_VISUALIZER_LAYERS,
  VisualizerLayerSchema,
  VisualizerSchema,
  formatIssues,
  type AvSession,
  type Project,
  type Scene,
  type VisualizerLayer,
  type VisualizerType,
} from './schemas';

/**
 * Pure, validated operations on a session's visualizer layers (Phase 4C). Each returns a
 * new Project or throws OpError and changes nothing; the session is re-validated with the
 * same schema and reference checks as every other session edit (`putSession`). Layer
 * edits live in `avSessions`, which the visual undo history records, so every operation
 * here is one undo step.
 */

const seedFrom = (id: string) => {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return (h >>> 0) % 2 ** 31;
};

function withLayers(project: Project, sessionId: string, layers: VisualizerLayer[]): Project {
  const s = sessionOf(project, sessionId);
  const parsed = VisualizerSchema.safeParse({ layers });
  if (!parsed.success) throw new OpError(`That change would make the visualizer invalid: ${formatIssues(parsed.error)[0] ?? 'unknown problem'}`);
  return putSession(project, { ...s, visualizer: parsed.data });
}

function layerIndex(s: AvSession, layerId: string): number {
  const i = s.visualizer.layers.findIndex((l) => l.id === layerId);
  if (i < 0) throw new OpError('That visualizer layer no longer exists.');
  return i;
}

const sceneOfSession = (project: Project, s: AvSession): Scene => {
  const scene = project.scenes.find((x) => x.id === s.sceneId);
  if (!scene) throw new OpError('The session’s scene no longer exists.');
  return scene;
};

/** Adds a default layer of `type` on top. */
export function addLayer(project: Project, sessionId: string, type: VisualizerType, id = createId('viz')): { project: Project; id: string } {
  const s = sessionOf(project, sessionId);
  if (s.visualizer.layers.length >= MAX_VISUALIZER_LAYERS) throw new OpError(`A visualizer can hold at most ${MAX_VISUALIZER_LAYERS} layers.`);
  const layer = defaultLayer(type, sceneOfSession(project, s), id, seedFrom(id));
  return { project: withLayers(project, sessionId, [...s.visualizer.layers, layer]), id };
}

/** Copies a layer (new id and seed, name "… copy") directly above the original. */
export function duplicateLayer(project: Project, sessionId: string, layerId: string, id = createId('viz')): { project: Project; id: string } {
  const s = sessionOf(project, sessionId);
  if (s.visualizer.layers.length >= MAX_VISUALIZER_LAYERS) throw new OpError(`A visualizer can hold at most ${MAX_VISUALIZER_LAYERS} layers.`);
  const i = layerIndex(s, layerId);
  const src = s.visualizer.layers[i]!;
  const copy = { ...structuredClone(src), id, seed: seedFrom(id), name: `${src.name} copy`.slice(0, 40) };
  const layers = [...s.visualizer.layers];
  layers.splice(i + 1, 0, copy);
  return { project: withLayers(project, sessionId, layers), id };
}

export function deleteLayer(project: Project, sessionId: string, layerId: string): Project {
  const s = sessionOf(project, sessionId);
  layerIndex(s, layerId);
  return withLayers(project, sessionId, s.visualizer.layers.filter((l) => l.id !== layerId));
}

/** Moves a layer up (+1, towards the front) or down (−1) in the order. */
export function moveLayer(project: Project, sessionId: string, layerId: string, delta: number): Project {
  const s = sessionOf(project, sessionId);
  const i = layerIndex(s, layerId);
  const j = Math.max(0, Math.min(s.visualizer.layers.length - 1, i + Math.round(delta)));
  if (i === j) return project;
  const layers = [...s.visualizer.layers];
  const [l] = layers.splice(i, 1);
  layers.splice(j, 0, l!);
  return withLayers(project, sessionId, layers);
}

export type LayerPatch = {
  [K in keyof VisualizerLayer]?: K extends 'style' | 'response' | 'params' ? Partial<VisualizerLayer[K]> : VisualizerLayer[K];
};

/** Edits a layer; nested `style`, `response` and `params` are merged. The type cannot change. */
export function updateLayer(project: Project, sessionId: string, layerId: string, patch: LayerPatch): Project {
  const s = sessionOf(project, sessionId);
  const i = layerIndex(s, layerId);
  const l = s.visualizer.layers[i]!;
  if (patch.type !== undefined && patch.type !== l.type) throw new OpError('A layer’s type cannot be changed; add a new layer instead.');
  if (patch.id !== undefined && patch.id !== l.id) throw new OpError('A layer’s id cannot be changed.');
  const next = {
    ...l,
    ...patch,
    style: { ...l.style, ...patch.style },
    response: { ...l.response, ...patch.response },
    params: { ...l.params, ...patch.params },
  };
  const parsed = VisualizerLayerSchema.safeParse(next);
  if (!parsed.success) throw new OpError(`That value is not allowed: ${formatIssues(parsed.error)[0] ?? 'unknown problem'}`);
  const layers = [...s.visualizer.layers];
  layers[i] = parsed.data;
  return withLayers(project, sessionId, layers);
}

export const setLayerVisible = (project: Project, sessionId: string, layerId: string, visible: boolean): Project => updateLayer(project, sessionId, layerId, { visible });

export interface ApplyPresetOptions {
  /** Use this scene (characters, animation…) instead of creating a visualizer backdrop scene. */
  sceneId?: string;
  /** Replace the layers of this session instead of creating a new one. */
  sessionId?: string;
  name?: string;
}

/**
 * Applies a built-in preset: copies its layers (fresh ids and seeds) into a session. By
 * default it creates a new session on a new backdrop scene in the preset's colours (a
 * visualizer-only composition); with `sceneId` it uses that scene (and its first
 * animation); with `sessionId` it replaces that session's layers. The preset definition
 * itself is a constant and is never modified.
 */
export function applyPreset(project: Project, presetId: string, options: ApplyPresetOptions = {}, ids: { session?: string; scene?: string } = {}): { project: Project; sessionId: string } {
  const preset = presetById(presetId);
  if (!preset) throw new OpError('That visualizer preset does not exist.');
  let p = project;
  let sessionId = options.sessionId;
  if (sessionId) {
    const s = sessionOf(p, sessionId);
    const layers = preset.layers(sceneOfSession(p, s)).map((l) => {
      const id = createId('viz');
      return { ...l, id, seed: seedFrom(id) };
    });
    return { project: withLayers(p, sessionId, layers), sessionId };
  }
  let sceneId = options.sceneId;
  if (!sceneId) {
    sceneId = ids.scene ?? createId('scn');
    const scene: Scene = { id: sceneId, name: `${preset.name} backdrop`, background: preset.backdrop.background, speed: 1, elements: [], ...sceneEditorDefaults(), gradient: preset.backdrop.gradient };
    p = addScene(p, scene);
  } else if (!p.scenes.some((x) => x.id === sceneId)) throw new OpError('That scene no longer exists.');
  const created = createSession(p, { sceneId, name: options.name ?? preset.name }, ids.session ?? createId('avs'));
  p = created.project;
  sessionId = created.id;
  const scene = p.scenes.find((x) => x.id === sceneId)!;
  const layers = preset.layers(scene).map((l) => {
    const id = createId('viz');
    return { ...l, id, seed: seedFrom(id) };
  });
  return { project: withLayers(p, sessionId, layers), sessionId };
}
